import { useMemo, useState } from "react";
import { Trash2, FileDown, Eye, Pencil, Send, AlertTriangle, ChevronUp, ChevronDown, ChevronsUpDown, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { useConfirm } from "@/context/ConfirmContext";
import { api, errMsg, pln, downloadBlob, fmtNip } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { ColorSwatch } from "@/components/ColorSwatch";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";

const STATUSES = ["Nowe zamówienie", "Wysłane do produkcji", "Zrealizowane", "Wysłane Garwolin", "Wysłane Nowy Sącz", "Wysłane do klienta", "Zamknięte", "Do weryfikacji"];
const INVOICE_ELIGIBLE = ["Zrealizowane", "Wysłane Garwolin", "Wysłane Nowy Sącz", "Wysłane do klienta", "Zamknięte", "Do weryfikacji"];

export const OrdersTable = ({ orders, isAdmin, onChanged, onView, onEdit }) => {
  const confirm = useConfirm();
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const SORT_COLS = [
    { key: "number", label: "Numer", align: "left", get: (o) => o.number || "", type: "str" },
    { key: "created_at", label: "Data", align: "left", get: (o) => o.created_at || "", type: "str" },
    ...(isAdmin ? [{ key: "employee_name", label: "Handlowiec", align: "left", get: (o) => o.employee_name || "", type: "str" }] : []),
    { key: "customer", label: "Klient", align: "left", get: (o) => (o.customer?.short_name || o.customer?.name || ""), type: "str" },
    { key: "items", label: "Pozycje", align: "left", sortable: false },
    { key: "total_qty", label: "Szt.", align: "right", get: (o) => o.total_qty || 0, type: "num" },
    { key: "total_net", label: "Netto", align: "right", get: (o) => o.total_net || 0, type: "num" },
    { key: "total_commission", label: "Prowizja", align: "right", get: (o) => o.total_commission || 0, type: "num" },
    { key: "status", label: "Status", align: "left", get: (o) => STATUSES.indexOf(o.status), type: "num" },
  ];
  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir(key === "created_at" ? "desc" : "asc"); }
  };
  const sorted = useMemo(() => {
    const col = SORT_COLS.find((c) => c.key === sortKey);
    if (!sortKey || !col || col.sortable === false) return orders;
    const arr = [...orders];
    arr.sort((a, b) => {
      const av = col.get(a), bv = col.get(b);
      const r = col.type === "num" ? (av - bv) : String(av).localeCompare(String(bv), "pl", { numeric: true });
      return sortDir === "asc" ? r : -r;
    });
    return arr;
  }, [orders, sortKey, sortDir, isAdmin]); // eslint-disable-line react-hooks/exhaustive-deps
  const staleInfo = (o) => {
    if (!["Nowe zamówienie", "Wysłane do produkcji"].includes(o.status)) return null;
    const base = o.updated_at || o.created_at;
    if (!base) return null;
    const days = Math.floor((Date.now() - new Date(base).getTime()) / 86400000);
    return days >= 14 ? days : null;
  };
  const remove = async (o) => {
    if (!(await confirm({ title: "Usunąć zamówienie?", description: `Czy na pewno usunąć zamówienie ${o.number}? Tej operacji nie można cofnąć.`, confirmText: "Usuń", danger: true }))) return;
    try { await api.delete(`/orders/${o.id}`); toast.success("Zamówienie usunięte"); onChanged(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const sendToProd = async (o) => {
    if (!(await confirm({ title: "Wysłać do produkcji?", description: `Wysłać zamówienie ${o.number} do produkcji? Trafi do kolejki produkcji.`, confirmText: "Wyślij" }))) return;
    try { await api.patch(`/orders/${o.id}/send-to-production`); toast.success(`${o.number}: wysłane do produkcji`); onChanged(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const setStatus = async (o, status) => {
    if (!(await confirm({ title: "Zmienić status zamówienia?", description: `Zmienić status zamówienia ${o.number} na „${status}”?`, confirmText: "Zmień status" }))) return;
    try { await api.patch(`/orders/${o.id}/status`, { status }); toast.success(`${o.number}: ${status}`); onChanged(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const setWapro = async (o, v) => {
    try { await api.patch(`/orders/${o.id}/wapro-produced`, { value: v }); toast.success(`${o.number}: WAPRO ${v ? "✓ wyprodukowane" : "— cofnięto"}`); onChanged(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const setInvoiced = async (o, v) => {
    try { await api.patch(`/orders/${o.id}/invoiced`, { value: v }); toast.success(`${o.number}: faktura ${v ? "wystawiona" : "cofnięta"}`); onChanged(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const setDtf = async (o, v) => {
    try { await api.patch(`/orders/${o.id}/dtf-printed`, { value: v }); toast.success(`${o.number}: wydruk DTF ${v ? "✓ wykonany" : "— cofnięto"}`); onChanged(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const SmsBadge = ({ o }) => {
    const hist = o.sms_history || [];
    if (!hist.length) return null;
    const delivered = hist.some((h) => (h.status || "").toUpperCase().includes("DELIVER") && !(h.status || "").toUpperCase().includes("UNDELIVER"));
    const title = `Wysłano SMS do klienta: ${hist.length}× (${hist.map((h) => h.status || "—").join(", ")})`;
    return (
      <span data-testid={`order-sms-badge-${o.number}`} title={title}
        className={`inline-flex shrink-0 items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-bold ${delivered ? "bg-emerald-500/15 text-emerald-300" : "bg-sky-500/15 text-sky-300"}`}>
        <MessageSquare className="h-3 w-3" />SMS{hist.length > 1 ? ` ×${hist.length}` : ""}
      </span>
    );
  };

  const OrderFlags = ({ o, suffix = "" }) => {
    const eligible = INVOICE_ELIGIBLE.includes(o.status);
    if (!isAdmin && !eligible && !o.invoiced) return null;
    return (
      <div className="flex flex-col gap-1 mt-1.5">
        {isAdmin && (
          <label data-testid={`order-dtf-${o.number}${suffix}`} className="flex items-center gap-1.5 text-[11px] cursor-pointer select-none">
            <Checkbox checked={!!o.dtf_printed} onCheckedChange={(v) => setDtf(o, !!v)} className="h-3.5 w-3.5" />
            <span className={o.dtf_printed ? "text-amber-400 font-semibold" : "text-muted-foreground"}>Wydruk DTF</span>
          </label>
        )}
        {isAdmin && (
          <span data-testid={`order-prodfiles-${o.number}${suffix}`} className="flex items-center gap-1.5 text-[11px] select-none" title="Automatyczne — zaznaczone, gdy do zamówienia dodano pliki produkcyjne">
            <Checkbox checked={!!(o.production_files?.length)} disabled className="h-3.5 w-3.5" />
            <span className={o.production_files?.length ? "text-indigo-300 font-semibold" : "text-muted-foreground"}>Pliki produkcyjne{o.production_files?.length ? ` (${o.production_files.length})` : ""}</span>
          </span>
        )}
        {isAdmin && (
          <label data-testid={`order-wapro-${o.number}${suffix}`} className="flex items-center gap-1.5 text-[11px] cursor-pointer select-none">
            <Checkbox checked={!!o.wapro_produced} onCheckedChange={(v) => setWapro(o, !!v)} className="h-3.5 w-3.5" />
            <span className={o.wapro_produced ? "text-emerald-400 font-semibold" : "text-muted-foreground"}>Wyprodukowane w WAPRO</span>
          </label>
        )}
        {(eligible || o.invoiced) && (
          <label data-testid={`order-invoiced-${o.number}${suffix}`} className={`flex items-center gap-1.5 text-[11px] select-none ${(eligible || o.invoiced) ? "cursor-pointer" : "cursor-not-allowed opacity-50"}`}>
            <Checkbox checked={!!o.invoiced} disabled={!eligible && !o.invoiced} onCheckedChange={(v) => setInvoiced(o, !!v)} className="h-3.5 w-3.5" />
            <span className={o.invoiced ? "text-sky-400 font-semibold" : "text-muted-foreground"}>Faktura wystawiona</span>
          </label>
        )}
      </div>
    );
  };

  if (!orders.length) return <div data-testid="orders-empty" className="rounded-xl border border-dashed border-border p-12 text-center text-muted-foreground">Brak zamówień spełniających kryteria.</div>;

  const Actions = ({ o, suffix = "" }) => (
    <div className="flex gap-1">
      <Button data-testid={`order-view-${o.number}${suffix}`} size="icon" variant="ghost" className="h-8 w-8" onClick={() => onView(o)} title="Szczegóły"><Eye className="h-4 w-4" /></Button>
      {(isAdmin || o.status === "Nowe zamówienie") && (
        <Button data-testid={`order-edit-${o.number}${suffix}`} size="icon" variant="ghost" className="h-8 w-8 text-amber-300 hover:text-amber-200" onClick={() => onEdit(o)} title="Edytuj"><Pencil className="h-4 w-4" /></Button>
      )}
      {o.status === "Nowe zamówienie" && (
        <Button data-testid={`order-send-prod-${o.number}${suffix}`} size="icon" variant="ghost" className="h-8 w-8 text-indigo-300 hover:text-indigo-200" onClick={() => sendToProd(o)} title="Wyślij do produkcji"><Send className="h-4 w-4" /></Button>
      )}
      <Button data-testid={`order-pdf-${o.number}${suffix}`} size="icon" variant="ghost" className="h-8 w-8" onClick={() => downloadBlob(`/orders/${o.id}/pdf`, `${o.number.replace(/\//g, "-")}.pdf`)} title="PDF"><FileDown className="h-4 w-4" /></Button>
      {isAdmin && (
        <Button data-testid={`order-delete-${o.number}${suffix}`} size="icon" variant="ghost" className="h-8 w-8 text-rose-400 hover:text-rose-300" onClick={() => remove(o)} title="Usuń"><Trash2 className="h-4 w-4" /></Button>
      )}
    </div>
  );

  const Items = ({ o }) => (
    <div className="flex flex-col gap-0.5">
      {o.items.map((it, i) => (
        <span key={i} className="flex items-center gap-1.5 truncate">
          <ColorSwatch name={it.color} size={12} />
          <span className="truncate">{it.sku} {it.color} ×{it.quantity}</span>
        </span>
      ))}
    </div>
  );

  return (
    <>
    {/* Mobile sort + cards */}
    <div className="md:hidden mb-3 flex items-center gap-2" data-testid="orders-mobile-sort">
      <Select value={sortKey || "default"} onValueChange={(v) => setSortKey(v === "default" ? null : v)}>
        <SelectTrigger data-testid="orders-mobile-sort-select" className="h-9 flex-1 text-xs"><SelectValue placeholder="Sortuj…" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="default">Sortowanie domyślne</SelectItem>
          {SORT_COLS.filter((c) => c.sortable !== false).map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
        </SelectContent>
      </Select>
      <Button variant="outline" size="icon" className="h-9 w-9 shrink-0" disabled={!sortKey} data-testid="orders-mobile-sort-dir" onClick={() => setSortDir((d) => (d === "asc" ? "desc" : "asc"))}>{sortDir === "asc" ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}</Button>
    </div>
    <div className="md:hidden space-y-3" data-testid="orders-cards">
      {sorted.map((o) => { const sd = staleInfo(o); return (
        <div key={o.id} data-testid={`order-card-${o.number}`} className={`rounded-xl border p-4 space-y-3 ${sd ? "border-rose-500/40 bg-rose-500/[0.05]" : "border-border bg-card"}`}>
          <div className="flex items-start justify-between gap-2">
            <div>
              <div className="font-mono text-xs font-bold text-amber-300">{o.number}</div>
              <div className="text-xs text-muted-foreground">{o.created_at.slice(0, 10)}{isAdmin ? ` · ${o.employee_name}` : ""}</div>
              {sd && <span data-testid={`order-stale-${o.number}-m`} className="mt-1 inline-flex items-center gap-1 rounded bg-rose-500/15 px-1.5 py-0.5 text-[10px] font-bold text-rose-300"><AlertTriangle className="h-3 w-3" />Zalega {sd} dni</span>}
            </div>
            {isAdmin ? (
              <Select value={o.status} onValueChange={(v) => setStatus(o, v)}>
                <SelectTrigger data-testid={`order-status-dropdown-${o.number}-m`} className="h-8 w-[150px] text-xs shrink-0"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            ) : <StatusBadge status={o.status} />}
          </div>
          <OrderFlags o={o} suffix="-m" />
          <div>
            <div className="flex items-center gap-2"><span className="font-semibold">{o.customer.short_name || o.customer.name}</span><SmsBadge o={o} /></div>
            <div className="text-xs text-muted-foreground font-mono">{fmtNip(o.customer.nip)}</div>
          </div>
          <div className="text-xs text-muted-foreground"><Items o={o} /></div>
          <div className="flex items-center justify-between border-t border-border pt-3 text-sm">
            <div className="flex gap-4">
              <div><div className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono">Szt.</div><div className="tabular-nums font-semibold">{o.total_qty}</div></div>
              <div><div className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono">Netto</div><div className="tabular-nums font-semibold">{pln(o.total_net)}</div></div>
              <div><div className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono">Prowizja</div><div className="tabular-nums font-semibold text-amber-300">{pln(o.total_commission)}</div></div>
            </div>
          </div>
          <div className="flex justify-end"><Actions o={o} suffix="-m" /></div>
        </div>
      ); })}
    </div>

    {/* Desktop table */}
    <div className="hidden md:block rounded-xl border border-border bg-card overflow-x-auto">
      <table className="w-full text-sm min-w-[900px]" data-testid="orders-table">
        <thead className="bg-secondary text-muted-foreground font-mono text-[11px] uppercase tracking-wider">
          <tr>
            {SORT_COLS.map((col) => (
              <th key={col.key} className={`px-3 py-2.5 ${col.align === "right" ? "text-right" : "text-left"}`}>
                {col.sortable === false ? col.label : (
                  <button type="button" data-testid={`sort-${col.key}`} onClick={() => toggleSort(col.key)}
                    className={`inline-flex items-center gap-1 uppercase tracking-wider hover:text-foreground transition-colors ${col.align === "right" ? "flex-row-reverse" : ""} ${sortKey === col.key ? "text-amber-300" : ""}`}>
                    {col.label}
                    {sortKey === col.key ? (sortDir === "asc" ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />) : <ChevronsUpDown className="h-3 w-3 opacity-40" />}
                  </button>
                )}
              </th>
            ))}
            <th className="px-3 py-2.5" />
          </tr>
        </thead>
        <tbody>
          {sorted.map((o) => { const sd = staleInfo(o); return (
            <tr key={o.id} data-testid={`order-row-${o.number}`} className={`border-t border-border transition-colors ${sd ? "bg-rose-500/[0.05] hover:bg-rose-500/10" : "hover:bg-accent/50"}`}>
              <td className="px-4 py-2.5 font-mono text-xs text-amber-300">
                {o.number}
                {sd && <span data-testid={`order-stale-${o.number}`} className="mt-1 flex w-fit items-center gap-1 rounded bg-rose-500/15 px-1.5 py-0.5 text-[10px] font-bold text-rose-300" title={`Brak zmiany statusu od ${sd} dni`}><AlertTriangle className="h-3 w-3" />Zalega {sd} dni</span>}
              </td>
              <td className="px-3 py-2.5 text-muted-foreground whitespace-nowrap">{o.created_at.slice(0, 10)}</td>
              {isAdmin && <td className="px-3 py-2.5">{o.employee_name}</td>}
              <td className="px-3 py-2.5"><div className="flex items-center gap-2"><span className="font-medium">{o.customer.short_name || o.customer.name}</span><SmsBadge o={o} /></div><div className="text-xs text-muted-foreground font-mono">{fmtNip(o.customer.nip)}</div></td>
              <td className="px-3 py-2.5 text-xs text-muted-foreground max-w-[240px]">
                <div className="flex flex-col gap-0.5">
                  {o.items.map((it, i) => (
                    <span key={i} className="flex items-center gap-1.5 truncate">
                      <ColorSwatch name={it.color} size={12} />
                      <span className="truncate">{it.sku} {it.color} ×{it.quantity}</span>
                    </span>
                  ))}
                </div>
              </td>
              <td className="px-3 py-2.5 text-right tabular-nums">{o.total_qty}</td>
              <td className="px-3 py-2.5 text-right tabular-nums">{pln(o.total_net)}</td>
              <td className="px-3 py-2.5 text-right tabular-nums text-amber-300 font-semibold">{pln(o.total_commission)}</td>
              <td className="px-3 py-2.5">
                {isAdmin ? (
                  <Select value={o.status} onValueChange={(v) => setStatus(o, v)}>
                    <SelectTrigger data-testid={`order-status-dropdown-${o.number}`} className="h-8 w-[150px] text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s} data-testid={`status-option-${s}`}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                ) : <StatusBadge status={o.status} />}
                <OrderFlags o={o} />
              </td>
              <td className="px-3 py-2.5">
                <div className="flex justify-end gap-1">
                  <Button data-testid={`order-view-${o.number}`} size="icon" variant="ghost" className="h-8 w-8" onClick={() => onView(o)} title="Szczegóły"><Eye className="h-4 w-4" /></Button>
                  {(isAdmin || o.status === "Nowe zamówienie") && (
                    <Button data-testid={`order-edit-${o.number}`} size="icon" variant="ghost" className="h-8 w-8 text-amber-300 hover:text-amber-200" onClick={() => onEdit(o)} title="Edytuj"><Pencil className="h-4 w-4" /></Button>
                  )}
                  {o.status === "Nowe zamówienie" && (
                    <Button data-testid={`order-send-prod-${o.number}`} size="icon" variant="ghost" className="h-8 w-8 text-indigo-300 hover:text-indigo-200" onClick={() => sendToProd(o)} title="Wyślij do produkcji"><Send className="h-4 w-4" /></Button>
                  )}
                  <Button data-testid={`order-pdf-${o.number}`} size="icon" variant="ghost" className="h-8 w-8" onClick={() => downloadBlob(`/orders/${o.id}/pdf`, `${o.number.replace(/\//g, "-")}.pdf`)} title="PDF"><FileDown className="h-4 w-4" /></Button>
                  {isAdmin && (
                    <Button data-testid={`order-delete-${o.number}`} size="icon" variant="ghost" className="h-8 w-8 text-rose-400 hover:text-rose-300" onClick={() => remove(o)} title="Usuń"><Trash2 className="h-4 w-4" /></Button>
                  )}
                </div>
              </td>
            </tr>
          ); })}
        </tbody>
      </table>
    </div>
    </>
  );
};
